
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { smartleadAPI } from '@/lib/smartlead-api';
import { format, subDays } from 'date-fns';

interface EmailAnalytics {
  today: number;
  yesterday: number;
  last_7_days: number;
  last_14_days: number;
  last_30_days: number;
  today_replies: number;
  yesterday_replies: number;
  last_7_days_replies: number;
  last_14_days_replies: number;
  last_30_days_replies: number;
  today_bounces: number;
  yesterday_bounces: number;
  last_7_days_bounces: number;
  last_14_days_bounces: number;
  last_30_days_bounces: number;
  daily_breakdown: Array<{
    date: string;
    total_sent: number;
    total_replies: number;
    total_bounces: number;
    unique_campaigns: number;
  }>;
}

export const useEmailAnalytics = (daysBack: number = 30) => {
  const queryClient = useQueryClient();

  // Set up real-time subscription for live updates
  useEffect(() => {
    const channelName = `email-analytics-${Math.random().toString(36).substr(2, 9)}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'obsync_dynamic_reports' },
        (payload) => {
          // Invalidate analytics query when new email events arrive
          if (['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'].includes(payload.new.event_type)) {
            console.log('📊 [useEmailAnalytics] Email event received, refreshing analytics...', payload.new.event_type);
            queryClient.invalidateQueries({ queryKey: ['emailAnalytics'] });
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🧹 [useEmailAnalytics] Cleaning up subscription');
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery({
    queryKey: ['emailAnalytics', daysBack],
    queryFn: async (): Promise<EmailAnalytics> => {
      // Check if Smartlead is configured - if not, return mock data
      if (!smartleadAPI.isConfigured()) {
        console.log('📊 [useEmailAnalytics] Smartlead API not configured, returning mock data for demo mode...');
        
        // Generate impressive and consistent demo data
        const generateDemoDailyAnalytics = (totalEmails30Days: number, todayEmails: number, yesterdayEmails: number) => {
          const dailyData = [];
          const now = new Date();

          // Day 0 (Today) - Most impressive day
          const day0Date = format(now, 'yyyy-MM-dd');
          const day0Sent = todayEmails;
          const day0Replies = Math.round(day0Sent * 0.123); // ~12.3% reply rate to match Dashboard Overview
          const day0Bounces = Math.round(day0Sent * 0.015); // ~1.5% bounce rate
          dailyData.push({
            date: day0Date,
            total_sent: day0Sent,
            total_replies: day0Replies,
            total_bounces: day0Bounces,
            unique_campaigns: 8 + Math.floor(Math.random() * 3), // 8-10 campaigns
          });

          // Day 1 (Yesterday) - Strong performance
          const day1Date = format(subDays(now, 1), 'yyyy-MM-dd');
          const day1Sent = yesterdayEmails;
          const day1Replies = Math.round(day1Sent * 0.125); // Slightly higher reply rate
          const day1Bounces = Math.round(day1Sent * 0.012); // Lower bounce rate
          dailyData.push({
            date: day1Date,
            total_sent: day1Sent,
            total_replies: day1Replies,
            total_bounces: day1Bounces,
            unique_campaigns: 10 + Math.floor(Math.random() * 3), // 10-12 campaigns
          });

          // Remaining 28 days - Generate realistic gradual trend
          const remainingEmails = totalEmails30Days - day0Sent - day1Sent;
          const avgRemainingDaily = remainingEmails / 28;

          // Create a gradual upward trend (more realistic)
          let totalGenerated = day0Sent + day1Sent;
          for (let i = 2; i < 30; i++) {
            const date = format(subDays(now, i), 'yyyy-MM-dd');
            
            // Calculate remaining emails to distribute
            const remainingDays = 30 - i;
            const remainingEmailsToDistribute = totalEmails30Days - totalGenerated;
            const avgForRemainingDays = remainingEmailsToDistribute / remainingDays;
            
            // Create gradual trend: older days have lower volumes, newer days higher
            const trendFactor = (30 - i) / 30; // 0.93 for day 2, 0.1 for day 29
            const baseVolume = avgRemainingDaily * (0.7 + trendFactor * 0.6); // 0.7x to 1.3x based on recency
            
            // Add realistic daily variation (±15% max)
            const variation = 0.85 + Math.random() * 0.3; // 0.85 to 1.15
            const maxAllowed = Math.min(avgForRemainingDays * 1.3, remainingEmailsToDistribute);
            const sent = Math.round(Math.min(baseVolume * variation, maxAllowed));
            
            const replies = Math.round(sent * (0.11 + Math.random() * 0.02)); // 11-13% reply rate
            const bounces = Math.round(sent * (0.01 + Math.random() * 0.005)); // 1-1.5% bounce rate
            
            dailyData.push({
              date: date,
              total_sent: sent,
              total_replies: replies,
              total_bounces: bounces,
              unique_campaigns: 5 + Math.floor(Math.random() * 5), // 5-9 campaigns
            });
            
            totalGenerated += sent;
          }

          return dailyData.reverse(); // Oldest to newest
        };

        const calculateSummary = (dailyData: any[]) => {
          const today = dailyData[dailyData.length - 1];
          const yesterday = dailyData[dailyData.length - 2];

          const last7DaysData = dailyData.slice(-7);
          const last14DaysData = dailyData.slice(-14);

          return {
            today: today.total_sent,
            yesterday: yesterday.total_sent,
            last_7_days: last7DaysData.reduce((sum, d) => sum + d.total_sent, 0),
            last_14_days: last14DaysData.reduce((sum, d) => sum + d.total_sent, 0),
            last_30_days: dailyData.reduce((sum, d) => sum + d.total_sent, 0),

            today_replies: today.total_replies,
            yesterday_replies: yesterday.total_replies,
            last_7_days_replies: last7DaysData.reduce((sum, d) => sum + d.total_replies, 0),
            last_14_days_replies: last14DaysData.reduce((sum, d) => sum + d.total_replies, 0),
            last_30_days_replies: dailyData.reduce((sum, d) => sum + d.total_replies, 0),

            today_bounces: today.total_bounces,
            yesterday_bounces: yesterday.total_bounces,
            last_7_days_bounces: last7DaysData.reduce((sum, d) => sum + d.total_bounces, 0),
            last_14_days_bounces: last14DaysData.reduce((sum, d) => sum + d.total_bounces, 0),
            last_30_days_bounces: dailyData.reduce((sum, d) => sum + d.total_bounces, 0),
          };
        };

        const totalEmails30Days = 28000; // Target for Last 30 Days (27-29k range)
        const todayEmails = 2847; // Fixed as per user request
        const yesterdayEmails = 2600; // Fixed as per user request

        const generatedDailyBreakdown = generateDemoDailyAnalytics(totalEmails30Days, todayEmails, yesterdayEmails);
        const summary = calculateSummary(generatedDailyBreakdown);

        const mockAnalytics: EmailAnalytics = {
          ...summary,
          daily_breakdown: generatedDailyBreakdown,
        };
        
        console.log(`📈 [useEmailAnalytics] Mock data - Today's metrics: Sent: ${mockAnalytics.today}, Replies: ${mockAnalytics.today_replies}, Bounces: ${mockAnalytics.today_bounces}`);
        return mockAnalytics;
      }

      console.log('📊 [useEmailAnalytics] Fetching email analytics using SQL CURRENT_DATE for consistency...');
      
      const { data, error } = await supabase
        .rpc('get_email_analytics', { days_back: daysBack });

      if (error) {
        console.error('❌ [useEmailAnalytics] Failed to fetch email analytics:', error);
        throw error;
      }

      console.log('📊 [useEmailAnalytics] Email analytics data received:', data);
      
      const analyticsData = data as unknown as EmailAnalytics;
      
      // Log current values for consistency verification with other components
      console.log(`📈 [useEmailAnalytics] Today's metrics (SQL CURRENT_DATE) - Sent: ${analyticsData.today}, Replies: ${analyticsData.today_replies}, Bounces: ${analyticsData.today_bounces}`);
      
      return analyticsData;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};

export const useRecentSyncReports = (limit: number = 10) => {
  return useQuery({
    queryKey: ['recentSyncReports', limit],
    queryFn: async () => {
      console.log('Fetching recent sync reports...');
      
      // Mock data since enhanced_sync_reports table doesn't exist
      const data = [{
        id: 'report_1',
        campaign_id: '1',
        sent_count: 100,
        open_count: 25,
        click_count: 5,
        reply_count: 3,
        bounce_count: 2,
        date: new Date().toISOString()
      }];

      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
};
